'use client';

/**
 * @fileoverview `DiscoverCacheSeeder` — client-side helper that seeds the RTK
 * Query `discoverEvents` cache with the SSR-fetched payload so cold-mount
 * subscribers (`DiscoverListView`, `DiscoverResultsBarConnected`) skip the
 * network and render straight from the cache.
 *
 * Without this seam every cold load of `/[city]/discover` fired three
 * `/events/discover` requests inside the first second and instantly tripped
 * the 60/15min anon-IP rate limit (audit 2026-06-10 #2). Pre-populating the
 * `discoverEvents` entry with the SSR response lets RTK Query satisfy both
 * the list-view and the results-bar subscriptions from one in-flight
 * promise — and because `keepUnusedDataFor` is 60s, the cache entry stays
 * warm long enough for the user's first scroll/interaction to land before
 * the next refetch.
 *
 * Seeds via `lociApi.util.upsertQueryData('discoverEvents', args, payload)`
 * inside a one-shot effect:
 *   - The dispatch must happen on the CLIENT (the store is per-client
 *     instance), so the component is wrapped in `'use client'` and seeds
 *     during `useEffect` to stay after-hydration safe.
 *   - The dependency array is empty by design — re-running with a stale
 *     `args` shape would overwrite a fresher entry the user has since
 *     refetched. The seeder is intentionally idempotent per mount and is
 *     re-instantiated by the template every time the URL state changes.
 *
 * @since API-1415 (Discover · de-duplicate SSR + client `/events/discover` fetches)
 */
import * as React from 'react';
import { useDispatch } from 'react-redux';

import {
  upsertDiscoverEventsQueryData,
  type DiscoverEventsQueryArgs,
  type DiscoverEventsResponse,
} from '@loci/api';
import type { AppDispatch } from '@loci/store';

export interface DiscoverCacheSeederProps {
  /**
   * Wire-format args used by the SSR fetch — must match what
   * {@link buildDiscoverQueryArgs} produces so RTK Query's structural
   * `serializeQueryArgs` lands on the same cache key the client subscribers
   * compute on mount. Any drift here re-introduces the duplicate fetch bug.
   */
  args: DiscoverEventsQueryArgs;
  /** SSR-fetched payload to seed into the `discoverEvents` cache entry. */
  payload: DiscoverEventsResponse;
}

/**
 * Render-less seeder. Mount as a sibling of `<DiscoverListView />` etc.; the
 * component renders `null` and performs all its work in `useEffect`.
 */
export function DiscoverCacheSeeder({ args, payload }: DiscoverCacheSeederProps): null {
  const dispatch = useDispatch<AppDispatch>();

  // Stable JSON encode the args so the dependency array is a primitive — the
  // args object identity changes on every render (it's produced via
  // `useMemo` in the parent) but its content is stable per URL state. We
  // only want to re-seed when the content changes.
  const argsKey = React.useMemo(() => JSON.stringify(args), [args]);
  // Refs let us reference the latest values inside the effect without
  // re-running it on every render — re-seeding on identity changes alone
  // would clobber a fresher entry the user has already refetched.
  const argsRef = React.useRef(args);
  const payloadRef = React.useRef(payload);
  argsRef.current = args;
  payloadRef.current = payload;

  React.useEffect(() => {
    // `upsertDiscoverEventsQueryData` writes the payload directly into the
    // cache entry for `(discoverEvents, args)`. Subsequent subscribers that
    // pass an equivalent args object find a fulfilled entry and skip the
    // outbound request. Typed helper (API-1720) — `injectEndpoints` does not
    // widen the declared `lociApi.util` surface.
    dispatch(upsertDiscoverEventsQueryData(argsRef.current, payloadRef.current));
    // Re-seed when the args content changes (URL state change → new SSR
    // payload). `argsRef`/`payloadRef` keep the effect closure in sync
    // without forcing a re-run on plain identity changes.
  }, [argsKey, dispatch]);

  return null;
}
